import { useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { getSocket, initSocket, disconnectSocket } from "../lib/socket"
import { useAuthStore } from "../stores/auth-store"

/**
 * Hook to initialize socket connection and handle real-time updates
 * Automatically invalidates relevant queries when events are received
 */
export function useSocketConnection() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()

  // React Router v7's useNavigate returns a new function reference whenever
  // the pathname changes. Holding it in a ref prevents the effect below from
  // re-running on every navigation — which would otherwise tear down the
  // socket (disconnectSocket in cleanup) and fire a spurious reconnect that
  // wipes active call state via the connect handler.
  const navigateRef = useRef(navigate)
  useEffect(() => {
    navigateRef.current = navigate
  }, [navigate])

  useEffect(() => {
    if (!isAuthenticated) {
      disconnectSocket()
      return
    }

    const socket = initSocket()

    // Matter events
    // Use ["matters", "list"] instead of ["matters"] to only invalidate listing queries.
    // ["matters"] prefix-matches ALL matter queries including detail/overview/related,
    // causing the viewed matter to refetch on every company-wide matter change.
    socket.on("matter:created", () => {
      queryClient.invalidateQueries({ queryKey: ["matters", "list"] })
    })

    socket.on("matter:updated", (matterId: string) => {
      queryClient.invalidateQueries({ queryKey: ["matters", "list"] })
      queryClient.invalidateQueries({ queryKey: ["matters", "detail", matterId] })
    })

    socket.on("matter:deleted", () => {
      queryClient.invalidateQueries({ queryKey: ["matters", "list"] })
    })

    // Matter detail events (uppercase format from server)
    socket.on("MATTERS.UPDATED", (data: { matter?: string }) => {
      if (data?.matter) {
        queryClient.invalidateQueries({ queryKey: ["matters", "detail", data.matter] })
      }
      queryClient.invalidateQueries({ queryKey: ["matters", "list"] })
    })

    socket.on("MATTERS.NUMBERS_UPDATED", (data: { matter?: string }) => {
      if (data?.matter) {
        queryClient.invalidateQueries({ queryKey: ["matters", "detail", data.matter] })
      }
    })

    // Notes/Feed events - only invalidate feed and communication, not the entire detail tree
    socket.on("NOTES.CREATED", (data: { matter?: string }) => {
      if (data?.matter) {
        queryClient.invalidateQueries({ queryKey: ["feed", data.matter] })
        queryClient.invalidateQueries({
          queryKey: ["matters", "detail", data.matter, "overview_communication"],
          exact: true,
        })
      }
    })

    // Document events
    socket.on("DOCUMENTS.CREATED", (data: { matter?: string }) => {
      if (data?.matter) {
        queryClient.invalidateQueries({ queryKey: ["documents", "matter", data.matter] })
      }
    })

    socket.on("DOCUMENTS.UPDATED", (data: { matter?: string }) => {
      if (data?.matter) {
        queryClient.invalidateQueries({ queryKey: ["documents", "matter", data.matter] })
      }
    })

    socket.on("DOCUMENTS.DELETED", (data: { matter?: string }) => {
      if (data?.matter) {
        queryClient.invalidateQueries({ queryKey: ["documents", "matter", data.matter] })
      }
    })

    // Document upload completed (after S3 processing)
    socket.on("DOCUMENTS.UPLOADED", (data: { matter?: string }) => {
      queryClient.invalidateQueries({ queryKey: ["outstanding_items"] })
      if (data?.matter) {
        queryClient.invalidateQueries({ queryKey: ["documents", "matter", data.matter] })
      }
    })

    // Outstanding item updated (e.g. document pushed to item)
    socket.on("OUTSTANDING_ITEMS.UPDATED", () => {
      queryClient.invalidateQueries({ queryKey: ["outstanding_items"] })
    })

    // Portal events (bankruptcy module)
    socket.on("PORTAL.UPDATED", (data: { matter?: string }) => {
      if (data?.matter) {
        queryClient.invalidateQueries({ queryKey: ["portal", "entry", data.matter] })
        queryClient.invalidateQueries({ queryKey: ["portal", "questionnaire", data.matter] })
        queryClient.invalidateQueries({ queryKey: ["portal", "ccc", data.matter] })
      }
    })

    socket.on("QUESTIONNAIRE.UPDATED", (data: { matter?: string }) => {
      if (data?.matter) {
        queryClient.invalidateQueries({ queryKey: ["portal", "questionnaire", data.matter] })
      }
    })

    // Automation events
    socket.on("AUTOMATIONS.UPDATED", (data: { matter?: string }) => {
      if (data?.matter) {
        queryClient.invalidateQueries({ queryKey: ["automations", "instances", data.matter] })
        queryClient.invalidateQueries({ queryKey: ["automations", "logs", data.matter] })
      }
    })

    // Contact events
    socket.on("contact:created", () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
    })

    socket.on("contact:updated", (contactId: string) => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
      queryClient.invalidateQueries({ queryKey: ["contact", contactId] })
    })

    // Task events
    socket.on("task:created", () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] })
    })

    socket.on("task:updated", () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] })
    })

    socket.on("task:completed", () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] })
    })

    // Notification events — new notification created
    socket.on("NOTIFICATIONS.CREATED", (notification: any) => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
      if (notification?.body) {
        const category = notification.category as string
        const opts = notification.action_url
          ? { action: { label: "View", onClick: () => navigateRef.current(notification.action_url) } }
          : {}
        if (["payment_failed"].includes(category)) {
          toast.error(notification.body, opts)
        } else if (["task_completed", "item_completed", "document_signed", "event_completed", "payment_received"].includes(category)) {
          toast.success(notification.body, opts)
        } else {
          toast.info(notification.body, opts)
        }
      }
    })

    // Notification count updates (for navbar badge)
    socket.on("NOTIFICATIONS.CHANGED", () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
    })

    // Sidebar count refresh events - invalidate when tasks, events, items change
    const sidebarRefreshEvents = [
      "TASKS.FINISHED",
      "TASKS.CREATED",
      "TASKS.UPDATED",
      "TASKS.DELETED",
      "EVENTS.FINISHED",
      "EVENTS.CREATED",
      "EVENTS.UPDATED",
      "EVENTS.DELETED",
      "GENERAL_ITEMS.CREATED",
      "GENERAL_ITEMS.UPDATED",
      "GENERAL_ITEMS.FINISHED",
      "MATTER_DOCUMENT_UPLOADS.UPDATED",
      "TEXT_THREADS.UPDATED",
    ]

    sidebarRefreshEvents.forEach((event) => {
      socket.on(event, () => {
        queryClient.invalidateQueries({ queryKey: ["sidebarCounts"] })
      })
    })

    // Division updates
    socket.on("DIVISIONS.CREATED", () => {
      queryClient.invalidateQueries({ queryKey: ["divisions"] })
    })
    socket.on("DIVISIONS.UPDATED", () => {
      queryClient.invalidateQueries({ queryKey: ["divisions"] })
    })
    socket.on("DIVISIONS.DELETED", () => {
      queryClient.invalidateQueries({ queryKey: ["divisions"] })
    })

    // Lead events
    socket.on("LEADS.CREATED", () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] })
      queryClient.invalidateQueries({ queryKey: ["sidebarCounts"] })
    })
    socket.on("LEADS.CLAIMED", () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] })
      queryClient.invalidateQueries({ queryKey: ["sidebarCounts"] })
    })

    // Text thread events
    socket.on("TEXT_THREADS.UPDATED", (data: { thread?: string }) => {
      // Invalidate thread list
      queryClient.invalidateQueries({ queryKey: ["textThreads", "list"] })
      // If we have a specific thread ID, invalidate that detail
      if (data?.thread) {
        queryClient.invalidateQueries({ queryKey: ["textThreads", "detail", data.thread] })
      }
    })

    // Matter detail events to clean up
    const matterDetailEvents = [
      "MATTERS.UPDATED",
      "MATTERS.NUMBERS_UPDATED",
      "NOTES.CREATED",
      "DOCUMENTS.CREATED",
      "DOCUMENTS.UPDATED",
      "DOCUMENTS.DELETED",
      "DOCUMENTS.UPLOADED",
      "PORTAL.UPDATED",
      "QUESTIONNAIRE.UPDATED",
      "AUTOMATIONS.UPDATED",
    ]

    return () => {
      // Clean up all listeners
      socket.off("matter:created")
      socket.off("matter:updated")
      socket.off("matter:deleted")
      matterDetailEvents.forEach((event) => {
        socket.off(event)
      })
      socket.off("contact:created")
      socket.off("contact:updated")
      socket.off("task:created")
      socket.off("task:updated")
      socket.off("task:completed")
      socket.off("NOTIFICATIONS.CREATED")
      socket.off("NOTIFICATIONS.CHANGED")
      sidebarRefreshEvents.forEach((event) => {
        socket.off(event)
      })
      socket.off("DIVISIONS.CREATED")
      socket.off("DIVISIONS.UPDATED")
      socket.off("DIVISIONS.DELETED")
      socket.off("LEADS.CREATED")
      socket.off("LEADS.CLAIMED")
      socket.off("TEXT_THREADS.UPDATED")
      socket.off("OUTSTANDING_ITEMS.UPDATED")
      disconnectSocket()
    }
  }, [isAuthenticated, queryClient])
}

/**
 * Hook to get the current socket instance
 */
export function useSocket() {
  return getSocket()
}
